import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_OBSERVABILITY_API ?? 'http://localhost:8110';

export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy';

export type DependencyHealth = {
  name: string;
  type: string;
  status: HealthStatus;
  latency_ms: number;
  errors?: number;
  checked_at: string;
};

export type HealthSnapshot = {
  overall: HealthStatus;
  dependencies: DependencyHealth[];
  queues: DependencyHealth[];
  databases: DependencyHealth[];
  caches: DependencyHealth[];
  providers: DependencyHealth[];
  marketplaces: DependencyHealth[];
  checked_at: string;
};

export type OperationsDashboard = {
  tenant_id: string;
  health: HealthSnapshot;
  total_metrics: number;
  total_traces: number;
  total_logs: number;
  active_alerts: number;
  open_incidents: number;
  runbook_count: number;
  slo_compliance_pct: number;
  error_budget_used_pct: number;
  platform_health?: PlatformHealth[];
  capacity_summary?: CapacityForecast[];
  recent_alerts?: Alert[];
  recent_incidents?: Incident[];
  otel_status: OTELExporterStatus;
  generated_at: string;
};

export type PlatformHealth = {
  module: string;
  endpoint?: string;
  status: HealthStatus;
  latency_ms: number;
  checked_at: string;
};

export type OTELExporterStatus = {
  enabled: boolean;
  exporters: { type: string; endpoint?: string; healthy: boolean; exported_count: number }[];
  sample_rate: number;
  last_export: string;
};

export type MetricDefinition = {
  id: string;
  name: string;
  type: string;
  description?: string;
  unit?: string;
  status: string;
  created_at: string;
};

export type TraceSpan = {
  id: string;
  trace_id: string;
  correlation_id?: string;
  type: string;
  service: string;
  operation: string;
  status: string;
  duration_ms: number;
  started_at: string;
  ended_at: string;
};

export type LogEntry = {
  id: string;
  level: string;
  service: string;
  message: string;
  correlation_id?: string;
  masked: boolean;
  timestamp: string;
};

export type Alert = {
  id: string;
  type: string;
  name: string;
  description?: string;
  severity: string;
  status: string;
  value?: number;
  threshold?: number;
  fired_at: string;
};

export type Incident = {
  id: string;
  title: string;
  description?: string;
  severity: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type RunbookStep = {
  order: number;
  title: string;
  description?: string;
  automated: boolean;
  command?: string;
};

export type Runbook = {
  id: string;
  title: string;
  description?: string;
  service?: string;
  severity?: string;
  steps: RunbookStep[];
  tags?: string[];
  status: string;
  version: number;
  created_at: string;
};

export type CapacityForecast = {
  id: string;
  category: string;
  current: number;
  projected: number;
  limit: number;
  unit: string;
  horizon_days: number;
  generated_at: string;
};

export type CreateRunbookInput = {
  title: string;
  description?: string;
  service?: string;
  severity?: string;
  steps: RunbookStep[];
  tags?: string[];
};

export const observabilityApi = {
  dashboard: () => fetchAPI<OperationsDashboard>(BASE, '/api/v1/observability/dashboard'),
  health: () => fetchAPI<HealthSnapshot>(BASE, '/api/v1/observability/health'),
  metrics: (limit?: number) => fetchAPI<MetricDefinition[]>(BASE, `/api/v1/observability/metrics${limit ? `?limit=${limit}` : ''}`),
  logs: (level?: string, limit = 100) => fetchAPI<LogEntry[]>(BASE, `/api/v1/observability/logs?limit=${limit}${level ? `&level=${level}` : ''}`),
  traces: (type?: string, limit = 100) => fetchAPI<TraceSpan[]>(BASE, `/api/v1/observability/traces?limit=${limit}${type ? `&type=${type}` : ''}`),
  alerts: (status?: string) => fetchAPI<Alert[]>(BASE, `/api/v1/observability/alerts${status ? `?status=${status}` : ''}`),
  incidents: (status?: string) => fetchAPI<Incident[]>(BASE, `/api/v1/observability/incidents${status ? `?status=${status}` : ''}`),
  capacity: () => fetchAPI<CapacityForecast[]>(BASE, '/api/v1/observability/capacity'),
  runbooks: () => fetchAPI<Runbook[]>(BASE, '/api/v1/observability/runbooks'),
  createRunbook: (body: CreateRunbookInput) =>
    fetchAPI<Runbook>(BASE, '/api/v1/observability/runbooks', { method: 'POST', body: JSON.stringify(body) }),
};
